import { Injectable } from '@angular/core';
import { CompanyStorage } from 'src/app/_core/company-storage';
import { TerminalDataService } from 'src/app/private/terminal/services/terminal-data.service';
import { MonitoringService } from 'src/app/services/monitoring.service';
import { TaxApiService } from './tax-api.service';
import { Cashier, ITaxConfig } from './tax-register-config.service';

export interface IStoreTaxRegisterConfig {
  erpStoreId: number;
  tin: string;
  ipn: string;
  singleTax: boolean;
  objectName: string;
  objectAddress: string;
  orgName: string;
  registerNumFiscal: string;
  registerNumLocal: number;
  registerName: string;
  registerClosed: boolean;
  cashier: Cashier;
  // All the register's cashiers, the primary one included.
  cashiers?: Cashier[];
}

const TAX_REGISTER_SERVER_CONFIG = 'TAX_REGISTER_SERVER_CONFIG';

@Injectable({
  providedIn: 'root',
})
export class PrroConfigService {
  private _config: ITaxConfig[] = [];

  constructor(
    private companyStorage: CompanyStorage,
    private taxApiService: TaxApiService,
    private monitoringService: MonitoringService,
    private terminalDataService: TerminalDataService,
  ) {}

  async updateTaxConfigs(companyId: string, storeId: number) {
    try {
      const res = (await this.taxApiService.getStorePrroConfigs(
        companyId,
        storeId,
      )) as IStoreTaxRegisterConfig[];
      await this.saveConfig(this.mapPrroConfigsToTaxConfigs(res));
    } catch (err) {
      this.monitoringService.logException(err, 1, {
        operation: 'updateTaxConfigs',
        storeId: storeId,
      });
    }
  }

  async saveConfig(newConfigs: ITaxConfig[]) {
    const storedConfigs = await this.loadConfigs();
    const configsToSave: ITaxConfig[] = [];

    for (let ntc of newConfigs) {
      const storedConfig = this.findConfigByOrg(
        storedConfigs,
        ntc.store.erpStoreId,
        ntc.tin,
        ntc.ipn,
      );
      if (storedConfig) {
        // The cashier chosen on the terminal stays while the server still lists it.
        const selectedCashier = storedConfig.cashier
          ? ntc.cashiers?.find((c) => c.id == storedConfig.cashier.id)
          : null;
        if (selectedCashier) {
          ntc.cashier = selectedCashier;
        }
        ntc.defaultTaxLetter = storedConfig.defaultTaxLetter;
        ntc.useDefaultTax = storedConfig.useDefaultTax;
        ntc.active = storedConfig.active ?? true;
      }

      configsToSave.push(ntc);
    }

    await this.persistConfigs(configsToSave);
  }

  async enableConfig(orgCode: string, erpStoreId: number) {
    const orgConfig = await this.getOrgConfig(orgCode, erpStoreId);
    orgConfig.active = true;
    return this.addOrUpdateTaxConfig(orgConfig);
  }

  async disableConfig(orgCode: string, erpStoreId: number) {
    const orgConfig = await this.getOrgConfig(orgCode, erpStoreId);
    orgConfig.active = false;
    return this.addOrUpdateTaxConfig(orgConfig);
  }

  async getStoreConfig(storeId: number) {
    await this.ensureConfigLoaded();
    return this._config.find(
      (c) => c.store.erpStoreId == storeId && c.active !== false,
    );
  }

  async getRegisterConfig(taxRegisterFiscalNumber: string) {
    await this.ensureConfigLoaded();
    return this._config.find(
      (c) =>
        c.active !== false &&
        c.taxRegister?.fiscalNumber == taxRegisterFiscalNumber,
    );
  }

  async getConfig() {
    await this.ensureConfigLoaded();
    return this._config;
  }

  async getActiveConfigs() {
    await this.ensureConfigLoaded();
    return this._config.filter((c) => c.active !== false);
  }

  async updateDefaultTax(
    orgCode: string,
    erpStoreId: number,
    taxLetter: string,
    isActive: boolean,
  ) {
    const orgConfig = await this.getOrgConfig(orgCode, erpStoreId);
    orgConfig.defaultTaxLetter = taxLetter;
    orgConfig.useDefaultTax = isActive;
    return this.addOrUpdateTaxConfig(orgConfig);
  }

  async selectCashier(orgCode: string, cashier: any, erpStoreId: number) {
    const orgConfig = await this.getOrgConfig(orgCode, erpStoreId);
    orgConfig.cashier = cashier;
    return this.addOrUpdateTaxConfig(orgConfig);
  }

  async clearCashier(orgCode: string, erpStoreId: number) {
    const orgConfig = await this.getOrgConfig(orgCode, erpStoreId);
    orgConfig.cashier = null;
    return this.addOrUpdateTaxConfig(orgConfig);
  }

  async getOrgConfig(orgCode: string, erpStoreId: number): Promise<ITaxConfig> {
    const configs = await this.loadConfigs();
    const orgConfig = this.findOrgConfig(configs, orgCode, erpStoreId);
    if (!orgConfig) {
      throw `Налаштуванна ПРРО для організації ${orgCode} не знеайдено.`;
    }

    return orgConfig;
  }

  private mapPrroConfigsToTaxConfigs(
    prroConfigs: IStoreTaxRegisterConfig[],
  ): ITaxConfig[] {
    return (prroConfigs || [])
      .filter((prro) => prro.registerNumFiscal && prro.cashier)
      .map((prro) => {
        const taxRegister = {
          fiscalNumber: prro.registerNumFiscal,
          localNumber: prro.registerNumLocal,
          name: prro.registerName,
          address: prro.objectAddress,
          objectName: prro.objectName,
          status: prro.registerClosed ? 'closed' : 'active',
          created: null,
        };

        const store = {
          id: null,
          erpStoreId: prro.erpStoreId,
          erpStoreName: this.getStoreName(prro.erpStoreId),
          typeObjectName: '',
          name: prro.objectName,
          toCode: 0,
          address: prro.objectAddress,
          stanObject: '',
          typeOfRights: '',
          accStart: '',
          distr: '',
          lastCh: '',
          territ: '',
          taxRegisters: [taxRegister],
        };

        return {
          tin: prro.tin,
          ipn: prro.ipn,
          orgFullName: prro.orgName,
          cashiers: this.uniqueCashiers([prro.cashier, ...(prro.cashiers || [])]),
          store,
          taxRegister,
          cashier: prro.cashier,
          defaultTaxLetter: '',
          useDefaultTax: false,
          taxObjectsError: false,
          isNewOrg: false,
          active: true,
        } as ITaxConfig;
      });
  }

  private uniqueCashiers(cashiers: Cashier[]): Cashier[] {
    const seen = new Set<string>();
    return cashiers.filter((c) => {
      if (!c?.id || seen.has(c.id)) {
        return false;
      }
      seen.add(c.id);
      return true;
    });
  }

  // The server sends no store name: take it from the terminal store or one of its departments.
  private getStoreName(erpStoreId: number): string {
    const store = this.terminalDataService.terminalSettings()?.store;
    if (!store) {
      return '';
    }

    if (store.id == erpStoreId) {
      return store.name || '';
    }

    const department = (store.departments || []).find(
      (d) => d.id == erpStoreId,
    );
    return department?.name || '';
  }

  private async loadConfigs(): Promise<ITaxConfig[]> {
    const storage = await this.companyStorage.repository<ITaxConfig>(
      TAX_REGISTER_SERVER_CONFIG,
    );
    return (await storage.getItems()) || [];
  }

  private async persistConfigs(configs: ITaxConfig[]) {
    const storage = await this.companyStorage.repository<ITaxConfig>(
      TAX_REGISTER_SERVER_CONFIG,
    );
    await storage.loadItems(configs);
    this._config = configs;
  }

  // Drops the configs kept in memory and reads them again from the storage.
  async reload(): Promise<ITaxConfig[]> {
    this._config = await this.loadConfigs();
    return this._config;
  }

  private async ensureConfigLoaded() {
    if (this._config?.length) {
      return;
    }

    this._config = await this.loadConfigs();
  }

  private findOrgConfig(
    configs: ITaxConfig[],
    orgCode: string,
    erpStoreId: number,
  ) {
    return configs.find(
      (c) =>
        c.store.erpStoreId == erpStoreId &&
        ((c.tin && c.tin == orgCode) || (c.ipn && c.ipn == orgCode)),
    );
  }

  private findConfigByOrg(
    configs: ITaxConfig[],
    erpStoreId: number,
    tin: string,
    ipn: string,
  ) {
    return configs.find(
      (c) =>
        c.store.erpStoreId == erpStoreId &&
        ((tin && c.tin == tin) || (ipn && c.ipn == ipn)),
    );
  }

  private async addOrUpdateTaxConfig(taxConfig: ITaxConfig) {
    const configs = await this.loadConfigs();
    const configsToSave: ITaxConfig[] = [];

    if (!configs.length) {
      configsToSave.push(taxConfig);
    } else {
      let updated = false;
      for (let tc of configs) {
        if (
          tc.store.erpStoreId == taxConfig.store.erpStoreId &&
          ((tc.tin && tc.tin == taxConfig.tin) ||
            (tc.ipn && tc.ipn == taxConfig.ipn))
        ) {
          configsToSave.push(taxConfig);
          updated = true;
        } else {
          configsToSave.push(tc);
        }
      }

      if (!updated) {
        configsToSave.push(taxConfig);
      }
    }

    return this.persistConfigs(configsToSave);
  }
}
